// ── vatCalc.js — 부가세 신고 자동 집계 (V17 / 세무 V2 + V19 한도) ───────
//
// 거래 데이터로부터 한 과세기간의 부가세 신고용 합계를 계산.
//
// 핵심 규칙:
// - 매출세액 = 과세매출 공급가액 × 10%
// - 매입세액 = 적격증빙 매입의 부가세 (transaction.vat)
// - 의제매입공제 = (사용자 확정한 면세 1차 농수축산물 합계) × 공제율
//   V19: 매출액 × 50%(일반) / 60%(간이) 한도 적용
//
// 의제매입공제율 (2024년 기준):
//   - 음식점 일반과세자: 8/108
//   - 음식점 간이과세자: 6/108
//   - 제조업 일반과세자: 4/104
//   - 기타 일반과세자: 2/102

export const PRESUMED_DEDUCTION_RATES = {
  general: 8 / 108,
  simplified: 6 / 108,
  manufacturing: 4 / 104,
  other: 2 / 102,
};

export function getPresumedRate(bizType) {
  return PRESUMED_DEDUCTION_RATES[bizType] || PRESUMED_DEDUCTION_RATES.general;
}

const QUALIFIED_EVIDENCE = new Set(['card', 'cash_receipt', 'tax_invoice']);
function isQualified(t) {
  if (t?.evidence?.isQualified) return true;
  const ev = t?.evidence?.type || t?.evidenceType;
  return QUALIFIED_EVIDENCE.has(ev);
}

function getPresumedAmount(t) {
  if (!isQualified(t)) return 0;
  const vt = t?.vatTreatment;
  if (!vt) return 0;
  if (Number(vt.presumedAmount) > 0) return Number(vt.presumedAmount);
  if (vt.presumed === true) {
    return Number(t.taxFreeAmount) || 0;
  }
  return 0;
}

function getPresumedPending(t) {
  if (!isQualified(t)) return false;
  if (t?.vatTreatment?.presumed === true) return false;
  if (t?.aiSuggestedPresumed === true) return true;
  return false;
}

/**
 * 과세기간의 부가세 신고용 합계 집계.
 */
export function aggregateVatPeriod(transactions, months, bizType = 'general') {
  const monthSet = new Set(months);
  const inPeriod = (t) => {
    const d = String(t?.date || '').slice(0, 7);
    return monthSet.has(d);
  };

  let salesSupply = 0;
  let salesVat = 0;
  let salesTaxFree = 0;

  let purchaseSupply = 0;
  let purchaseVat = 0;
  let purchaseTaxFree = 0;

  let purchaseNonQualified = 0;

  let presumedConfirmed = 0;
  let presumedPendingCount = 0;
  let presumedPendingAmount = 0;

  const presumedTransactions = [];

  for (const t of (transactions || [])) {
    if (!t || !inPeriod(t)) continue;
    const supply = Number(t.supply) || 0;
    const vat = Number(t.vat) || 0;
    const taxFree = Number(t.taxFreeAmount) || 0;

    if (t.type === 'income') {
      const taxableSupply = Math.max(0, supply - taxFree);
      salesSupply += taxableSupply;
      salesVat += vat;
      salesTaxFree += taxFree;
    } else if (t.type === 'expense') {
      if (isQualified(t)) {
        const taxableSupply = Math.max(0, supply - taxFree);
        purchaseSupply += taxableSupply;
        purchaseVat += vat;
        purchaseTaxFree += taxFree;
      } else {
        purchaseNonQualified += (supply + vat);
      }

      const presumedAmt = getPresumedAmount(t);
      if (presumedAmt > 0) {
        presumedConfirmed += presumedAmt;
        presumedTransactions.push({ id: t.id, status: 'confirmed', amount: presumedAmt, t });
      } else if (getPresumedPending(t)) {
        presumedPendingCount += 1;
        const pendingAmt = Number(t.taxFreeAmount) || 0;
        presumedPendingAmount += pendingAmt;
        presumedTransactions.push({ id: t.id, status: 'pending', amount: pendingAmt, t });
      }
    }
  }

  const presumedRate = getPresumedRate(bizType);
  const presumedDeductionRaw = Math.round(presumedConfirmed * presumedRate);

  // V19: 의제매입공제 한도 — 매출 공급가액 × 한도율
  // 음식점업 한도: 일반 50%, 간이 60% (2024년, 매출 4억 이하)
  const limitRate = bizType === 'simplified' ? 0.60 : 0.50;
  const presumedLimit = Math.round(salesSupply * limitRate * presumedRate);
  const presumedDeduction = Math.min(presumedDeductionRaw, presumedLimit);
  const presumedLimitExceeded = presumedDeductionRaw > presumedLimit;

  const payable = salesVat - purchaseVat - presumedDeduction;

  return {
    salesSupply,
    salesVat,
    salesTaxFree,
    purchaseSupply,
    purchaseVat,
    purchaseTaxFree,
    purchaseNonQualified,
    presumedConfirmed,
    presumedPendingCount,
    presumedPendingAmount,
    presumedRate,
    presumedDeductionRaw,
    presumedLimit,
    presumedLimitExceeded,
    limitRate,
    presumedDeduction,
    payable,
    presumedTransactions,
  };
}
